<template>
  <BContainer class="mt-5">
    <!-- Error Panel -->
    <ErrorPanel
      :error-message="errorMessage"
      :error-title="errorTitle"
      :error-details="errorDetails"
      @clear="clearError"
    />

    <!-- Header -->
    <UserManagementHeader @create-user="showCreateModal = true" />

    <!-- Search Bar -->
    <UserSearchBar
      :search-query="searchQuery"
      @update:search-query="searchQuery = $event"
      @reset-search="resetSearch"
    />

    <!-- Loading Spinner -->
    <LoadingSpinner v-if="loading" />

    <!-- Users Table -->
    <UsersTable
      :users="users"
      :total="total"
      :page="page"
      :loading="loading"
      :pending-toggle-user-id="pendingUserId"
      @toggle-status="toggleUserStatus"
      @reset-password="confirmChangePassword"
      @update:page="page = $event"
    />

    <!-- Create User Modal -->
    <CreateUserModal
      :visible="showCreateModal"
      :form="createForm"
      :creating="creatingUser"
      @update:visible="showCreateModal = $event"
      @submit="submitCreate"
      @update:form="(field, value) => createForm[field as keyof typeof createForm] = value"
    />

    <!-- Temp Password Modal -->
    <TempPasswordModal
      :visible="showTempPasswordModal"
      :data="tempPasswordData"
      @update:visible="showTempPasswordModal = $event"
      @copy="copyToClipboard"
    />

    <!-- Confirm Password Reset Modal -->
    <ConfirmPasswordResetModal
      :visible="showConfirmPasswordModal"
      @update:visible="showConfirmPasswordModal = $event"
      @confirm="proceedChangePassword"
    />

    <!-- Change Password Modal -->
    <PasswordModal
      :visible="showPasswordModal"
      :data="passwordData"
      @update:visible="showPasswordModal = $event"
      @copy="copyToClipboard"
    />
  </BContainer>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { BContainer } from "bootstrap-vue-next";
import { useConfigStore } from "@/store";
import { useUserApi } from "@/composables/useUserApi";
import type { User, CreateUserData } from "@/composables/useUserApi";
import { generateRandomPassword } from "@/utils/password";
import { handleUserManagementError } from "@/utils/errorHandler";
import { useErrorPanel } from "@/composables/useErrorPanel";
import ErrorPanel from "@/components/ErrorPanel.vue";
import UserManagementHeader from "./components/UserManagement/UserManagementHeader.vue";
import UserSearchBar from "./components/UserManagement/UserSearchBar.vue";
import LoadingSpinner from "./components/widgets/LoadingSpinner.vue";
import UsersTable from "./components/UserManagement/UsersTable.vue";
import CreateUserModal from "./components/UserManagement/CreateUserModal.vue";
import TempPasswordModal from "./components/UserManagement/TempPasswordModal.vue";
import ConfirmPasswordResetModal from "./components/UserManagement/ConfirmPasswordResetModal.vue";
import PasswordModal from "./components/UserManagement/PasswordModal.vue";

const userApi = useUserApi();
const configStore = useConfigStore();
const pendingUserId = ref<string | null>(null);
const { errorMessage, errorTitle, errorDetails, clearError, setError } = useErrorPanel();

// Data state
const users = ref<User[]>([]);
const total = ref(0);
const page = ref(1);
const loading = ref(false);
const searchQuery = ref("");
const creatingUser = ref(false);

const showCreateModal = ref(false);
const showTempPasswordModal = ref(false);
const showPasswordModal = ref(false);
const showConfirmPasswordModal = ref(false);
const pendingPasswordUserId = ref<string | null>(null);
const pendingPasswordUsername = ref<string | null>(null);

const createForm = ref<CreateUserData>({
  username: "",
  email: "",
  display_name: "",
  password: "",
});

const tempPasswordData = ref<{ username: string; temp_password: string } | null>(null);
const passwordData = ref<{ username: string; temp_password: string } | null>(null);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

// Search debounce delay in milliseconds
const SEARCH_DEBOUNCE_DELAY_MS = 100;

const loadUsers = async () => {
  loading.value = true;
  try {
    const pageIndex = page.value - 1;
    const response = await userApi.listUsers(searchQuery.value || undefined, pageIndex);
    users.value = response.users;
    total.value = response.total;
  } catch (error) {
    console.error("Failed to load users:", error);
  } finally {
    loading.value = false;
  }
};

const debouncedSearch = () => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    page.value = 1;
    loadUsers();
  }, SEARCH_DEBOUNCE_DELAY_MS);
};

watch(searchQuery, () => {
  debouncedSearch();
});

const resetSearch = async () => {
  searchQuery.value = "";
  page.value = 1;
  await loadUsers();
};

const submitCreate = async (formData: { username: string; email: string; display_name: string; password: string }) => {
  // Modal handles all validation and error display
  // This is called only on successful user creation
  tempPasswordData.value = {
    username: formData.username,
    temp_password: formData.password,
  };
  showCreateModal.value = false;
  showTempPasswordModal.value = true;
  createForm.value = { username: "", email: "", display_name: "", password: "" };
  await loadUsers();
};

const toggleUserStatus = async (user: User) => {
  if (pendingUserId.value === user.id) return;
  clearError();
  pendingUserId.value = user.id;
  try {
    const newIsActive = !user.is_active;
    await userApi.updateUserStatus(user.id, newIsActive);
    const index = users.value.findIndex(u => u.id === user.id);
    if (index !== -1) {
      users.value[index].is_active = newIsActive;
    }
    const displayKey = user.display_name || user.username;
    if (newIsActive) {
      configStore.addUser(displayKey);
    } else {
      configStore.removeUser(displayKey);
    }
  } catch (error) {
    const operation = user.is_active ? "disable" : "enable";
    const message = handleUserManagementError(error, operation);
    setError(`Failed to ${operation} user`, message);
  } finally {
    pendingUserId.value = null;
  }
};

const confirmChangePassword = (userId: string) => {
  const user = users.value.find(u => u.id === userId);
  pendingPasswordUserId.value = userId;
  pendingPasswordUsername.value = user?.username || userId;
  showConfirmPasswordModal.value = true;
};

const proceedChangePassword = async () => {
  if (!pendingPasswordUserId.value) return;
  
  clearError();
  const generatedPassword = generateRandomPassword();
  
  try {
    await userApi.changeUserPassword(pendingPasswordUserId.value, generatedPassword);
    passwordData.value = {
      username: pendingPasswordUsername.value || pendingPasswordUserId.value,
      temp_password: generatedPassword,
    };
    showConfirmPasswordModal.value = false;
    showPasswordModal.value = true;
    await loadUsers();
  } catch (error) {
    const message = handleUserManagementError(error, "changePassword");
    setError("Error", message);
  } finally {
    pendingPasswordUserId.value = null;
    pendingPasswordUsername.value = null;
  }
};

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

loadUsers();
</script>

